import { AppBar as MuiAppBar, Stack, Toolbar } from '@mui/material';
import { ComponentProps, PropsWithChildren, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  Logomark,
  LogomarkSize,
  LogomarkVariant,
} from '@/components/brand/Logomark/Logomark';
import { UnstyledNavLink } from '@/components/navigation/UnstyledNavLink';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { WithClasses } from '@/styles/types';
import { zIndexes } from '@/styles/zIndexes';

import { HOUSEHOLD_SIDEBAR_WIDTH } from '../../Sidebar/HouseholdSidebar';
import { APPBAR_HEIGHT } from './AppBar.constants';

export const appbarHeight = APPBAR_HEIGHT;

const useStyles = makeStyles()((theme) => ({
  appBar: {
    height: APPBAR_HEIGHT,
    minHeight: APPBAR_HEIGHT,
    zIndex: zIndexes.MENU,
  },
  toolBar: {
    backgroundColor: theme.palette.primary.dark,
    paddingRight: theme.spacing(2),
  },
}));

export type AppBarProps = PropsWithChildren<{
  muiAppBarProps?: ComponentProps<typeof MuiAppBar>;
  classes?: WithClasses<typeof useStyles>;
  showLogomark?: boolean;
}>;

export function AppBar({
  muiAppBarProps = {},
  classes: externalClasses,
  showLogomark = true,
  children,
}: AppBarProps) {
  const { classes } = useStyles(undefined, {
    props: { classes: externalClasses },
  });
  const paddingLeft = useMemo(() => {
    const padding = showLogomark ? 0 : 2;

    return {
      xs: padding,
      sm: padding,
      md: padding,
      lg: padding,
      xl: padding,
    };
  }, [showLogomark]);

  return (
    <MuiAppBar
      data-testid="AppMenuBar"
      position="fixed"
      className={classes.appBar}
      {...muiAppBarProps}
    >
      <Toolbar
        disableGutters
        className={classes.toolBar}
        sx={{
          paddingLeft,
          minHeight: {
            xs: APPBAR_HEIGHT,
            sm: APPBAR_HEIGHT,
            md: APPBAR_HEIGHT,
            lg: APPBAR_HEIGHT,
            xl: APPBAR_HEIGHT,
          },
        }}
      >
        <Stack direction="row" alignItems="center" width="100%">
          {showLogomark && (
            <Stack
              direction="row"
              width={`${HOUSEHOLD_SIDEBAR_WIDTH}px`}
              justifyContent="center"
              height={APPBAR_HEIGHT}
              alignItems="center"
            >
              <UnstyledNavLink
                to={getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLDS_LIST, {})}
              >
                <Logomark
                  size={LogomarkSize.Wide}
                  variant={LogomarkVariant.Secondary}
                  imageSx={{
                    maxHeight: 48,
                    maxWidth: 140,
                    display: 'flex',
                  }}
                />
              </UnstyledNavLink>
            </Stack>
          )}
          {children}
        </Stack>
      </Toolbar>
    </MuiAppBar>
  );
}
